import { InterpolationWithTheme } from '@emotion/core';
import { Box, Button, Heading } from 'lucid-components-react';
import React, { useContext } from 'react';
import ReactHtmlParser from 'react-html-parser';

import Link from 'components/link/link';
import LucidContainer from 'components/lucid-container/lucid-container';
import LayoutContext from 'contexts/layoutContext';
import {
  Drupal_FieldNodeEduFieldBackNForthSection,
  Drupal_FieldNodeEnterpriseFieldBackNForthSection,
  Drupal_FieldNodePartnersFieldBackNForthSection,
  Drupal_ParagraphBackNForthSection,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphMarketoForm,
  Drupal_ParagraphSeoImage
} from 'generated/graphql';
import useMarketoLink from 'hooks/use-marketo-link';
import { LucidTheme } from 'types/types';
import { getProcessedValue } from 'utils/helpers';
import linkParser from 'utils/link-parser';

interface CustomJumplinks {
  text: string;
  search: string;
}

interface BackNForthProps {
  sections:
  | Drupal_FieldNodePartnersFieldBackNForthSection[]
  | Drupal_FieldNodeEnterpriseFieldBackNForthSection[]
  | Drupal_FieldNodeEduFieldBackNForthSection[]
  | null
  | undefined;
  useJumplinks?: boolean | null;
  customJumplinks?: CustomJumplinks[];
  largeLinks?: boolean;
}

enum BLOCK_OPTIONS {
  DEFAULT = 'nearest',
  START = 'start',
  CENTER = 'center',
  END = 'end'
}

const BackNForth = ({
  sections,
  useJumplinks,
  customJumplinks,
  largeLinks
}: BackNForthProps) => {
  const scrollTo = (selector: string, block: BLOCK_OPTIONS) => {
    const el = document.querySelector(selector);
    el!.scrollIntoView({
      behavior: 'smooth',
      block,
      inline: 'center'
    });
  };

  const { language } = useContext(LayoutContext);

  const getMarketoLink = useMarketoLink(language);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const buttonStyles: InterpolationWithTheme<any> = (theme: LucidTheme) => ({
    padding: `${theme.space[4]}px ${theme.space[0]}px !important`,
    fontWeight: theme.fontWeights.bolder,
    textAlign: 'center',
    fontSize: largeLinks ? '1.3rem' : '1rem',
    '&:last-child': {
      marginRight: theme.space[0]
    },
    [`@media only screen and (max-width: ${theme.breakpoints.tablet})`]: {
      flex: '1 0 50%'
    },
    [`@media only screen and (max-width: ${theme.breakpoints.mobile})`]: {
      padding: `${theme.space[4]}px ${theme.space[0]}px`,
      flex: 'none',
      width: '100%'
    }
  });

  return (
    <LucidContainer
      as="section"
      css={(theme: LucidTheme) => ({
        marginBottom: theme.space[8]
      })}
    >
      {useJumplinks && (
        <Box
          css={theme => ({
            listStyle: 'none',
            width: '100%',
            margin: `0 auto ${theme.space[8]}px`,
            paddingBottom: theme.space[3],
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            borderBottom: `2px solid ${theme.colors.orange[45]}`
          })}
        >
          {sections!.map((section, i: number) => {
            const sectionEntity = section.entity as Drupal_ParagraphBackNForthSection;
            return (
              <Button
                key={sectionEntity.fieldTitle}
                asLink
                css={buttonStyles}
                onClick={() =>
                  scrollTo(`.bnf-section-${i}`, BLOCK_OPTIONS.CENTER)}
                underline="none"
              >
                {sectionEntity.fieldTitle}
              </Button>
            );
          })}
          {customJumplinks &&
            customJumplinks.map(custom => (
              <button
                key={custom.text}
                css={buttonStyles}
                onClick={() => scrollTo(custom.search, BLOCK_OPTIONS.START)}
                type="button"
              >
                {custom.text}
              </button>
            ))}
        </Box>
      )}

      {sections!.map((section, i: number) => {
        const sectionEntity = section.entity as Drupal_ParagraphBackNForthSection;
        const sectionImage = sectionEntity.fieldSectionImage!
          .entity as Drupal_ParagraphSeoImage;
        return (
          <Box
            key={sectionEntity.fieldTitle}
            as="article"
            className={`bnf-section-${i}`}
            css={theme => ({
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              marginTop: theme.space[8],
              [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                display: 'block'
              },
              '&:first-of-type': {
                marginTop: 0
              },
              '&:nth-of-type(odd)': {
                figure: {
                  order: 2
                },
                '.back-n-forth__text': {
                  order: 1
                }
              }
            })}
          >
            <figure
              css={theme => ({
                margin: theme.space[0],
                textAlign: 'center',
                flex: '0 1 47%',
                [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                  flex: '0 1 100%',
                  maxWidth: 400,
                  marginBottom: theme.space[7]
                }
              })}
            >
              <img
                alt={sectionImage.fieldAltText!}
                src={sectionImage.fieldImageLink!}
              />
            </figure>
            <Box
              css={theme => ({
                flex: '0 1 47%',
                marginBottom: theme.space[8],
                color: theme.colors.coolGrey[90],
                [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                  flex: '0 1 100%',
                  maxWidth: 500
                },
                '&:last-child': {
                  marginBottom: theme.space[0]
                },
                p: {
                  fontWeight: theme.fontWeights.bold
                }
              })}
            >
              <Heading as="h2" mt={0} variant="subheadS">
                {sectionEntity.fieldTitle}
              </Heading>
              <Box>
                {ReactHtmlParser(
                  getProcessedValue(sectionEntity.fieldSectionDescription),
                  {
                    transform: linkParser
                  }
                )}
              </Box>
              {sectionEntity.fieldCtaButton &&
                sectionEntity.fieldCtaButton.length > 1 &&
                sectionEntity.fieldCtaButton[0]!.entity &&
                sectionEntity.fieldCtaButton.map((cta, j) => {
                  const button = cta!.entity as Drupal_ParagraphCtaButton;
                  const buttonMarketo = button.fieldMarketo!
                    .entity as Drupal_ParagraphMarketoForm;
                  const { url, copy } = getMarketoLink(
                    buttonMarketo.fieldMarketoId
                  );
                  return button.fieldButtonLink ? (
                    <Link
                      key={button.fieldButtonText}
                      asButton={!button.fieldIsSecondary}
                      css={{ ...(j === 1 && { marginLeft: 10 }) }}
                      primary={!button.fieldIsSecondary}
                      secondary={!!button.fieldIsSecondary}
                      to={button.fieldButtonLink}
                    >
                      {button.fieldButtonText}
                    </Link>
                  ) : (
                      /* eslint-disable-next-line react/jsx-indent */
                      <Link
                        key={button.fieldButtonText}
                        primary
                        asButton={!button.fieldIsSecondary}
                        css={{ ...(j === 1 && { marginLeft: 10 }) }}
                        to={url}
                      >
                        {button.fieldButtonText || copy}
                      </Link>
                    );
                })}
            </Box>
          </Box>
        );
      })}
    </LucidContainer>
  );
};

export default BackNForth;
